import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Pager, ConfirmModal, PageSpinner } from '@/components/common';
import { useFundamentalSurveys, usePatchSurveyStatus } from '@/hooks/useSurveys';
import type { Survey } from '@/types/survey.types';
import { SurveyStatusBadge } from '../../components/SurveyStatusBadge';
import { ColFilterPopover, FltBtn } from '../../components/ColFilterPopover';
import { TrashModal } from '../../components/TrashModal';
import type { ActiveFilterState } from '../../components/ColFilterPopover';
import type { TrashItem } from '@/types/survey.types';
import type { ConfirmModalConfig } from '@/types/common.types';
import { sortSurveysByPriority } from './sortSurveys';
import { useOuNameResolver, usePersonNameResolver } from '@/views/AnalyticsView/hooks/useAnalyticsNames';
import { formatOuAudience } from './formatOuAudience';
import { frequencyKey, frequencyLabel } from './surveyFrequency';
import { CreatedByCell } from './CreatedByCell';

function formatDate(iso: string, lang: string) {
  return new Date(iso).toLocaleDateString(lang, { day: 'numeric', month: 'short', year: 'numeric' });
}

interface FundamentalsTableProps {
  search: string;
  hidden: Set<string>;
  onOpenBuilder: (m: { mode: 'create' } | { mode: 'edit'; surveyId: string }) => void;
  onDuplicate?: (id: string) => void;
  onExportExcel?: (id: string) => void;
  deletedIds: Set<string>;
  trash: TrashItem[];
  onMoveToTrash: (id: string, name: string, responses: number) => void;
  onRestoreFromTrash: (id: string) => void;
  onDeleteForever: (id: string) => void;
  showTrash: boolean;
  onCloseTrash: () => void;
  /** Optional surveys override — if omitted, falls back to mock data */
  surveys?: Survey[];
  /** Whether any survey mutation (delete/restore/duplicate) is in flight */
  mutationPending?: boolean;
}

export function FundamentalsTable({
  search,
  hidden,
  onOpenBuilder,
  onDuplicate,
  onExportExcel,
  deletedIds,
  trash,
  onMoveToTrash,
  onRestoreFromTrash,
  onDeleteForever,
  showTrash,
  onCloseTrash,
  surveys: surveysProp,
  mutationPending = false,
}: FundamentalsTableProps) {
  const { t, i18n } = useTranslation();
  const { data: fetchedSurveys, isError, isLoading } = useFundamentalSurveys();
  const SURVEYS = surveysProp ?? fetchedSurveys ?? [];
  const patchStatus = usePatchSurveyStatus();
  const { resolveOuName } = useOuNameResolver();
  const creatorIds = useMemo(
    () => SURVEYS.map(s => s.createdBy).filter((creatorId): creatorId is string => !!creatorId && creatorId !== 'unknown'),
    [SURVEYS],
  );
  const { resolvePersonName } = usePersonNameResolver(creatorIds);

  const [surveyStatuses, setSurveyStatuses] = useState<Record<string, string>>({});
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [activeFilter, setActiveFilter] = useState<ActiveFilterState | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [confirmOpts, setConfirmOpts] = useState<ConfirmModalConfig | null>(null);

  function getStatus(s: { id: string; status: string }) { return surveyStatuses[s.id] ?? s.status; }
  function toggleStatus(id: string, currentStatus: string) {
    const next = currentStatus === 'active' ? 'paused' : 'active';
    setSurveyStatuses(prev => ({ ...prev, [id]: next }));
    patchStatus.mutate({ id, action: currentStatus === 'active' ? 'pause' : 'activate' });
  }

  function setFilter(col: string, val: string) {
    setFilters(prev => ({ ...prev, [col]: val }));
    setPage(1);
  }

  function openFilter(col: string, e: React.MouseEvent<HTMLButtonElement>) {
    const rect = e.currentTarget.getBoundingClientRect();
    setActiveFilter(prev => prev?.col === col ? null : { col, top: rect.bottom + 4, left: rect.left });
  }

  const active = useMemo(() => {
    const f = filters;
    return SURVEYS.filter(s => {
      if (deletedIds.has(s.id)) return false;
      if (search && !s.name.toLowerCase().includes(search.toLowerCase())) return false;
      if (f.name && !s.name.toLowerCase().includes(f.name.toLowerCase())) return false;
      if (f.status && s.status !== f.status) return false;
      if (f.audience && s.audience !== f.audience) return false;
      if (f.frequency && frequencyKey(s.activeDays) !== f.frequency) return false;
      return true;
    });
  }, [search, deletedIds, filters, SURVEYS]);

  const sorted = useMemo(
    () => sortSurveysByPriority(active, s => surveyStatuses[s.id] ?? s.status, s => s.periodStart),
    [active, surveyStatuses],
  );

  const sliced = useMemo(() => sorted.slice((page - 1) * pageSize, page * pageSize), [sorted, page, pageSize]);

  const flt = (col: string) => ({
    col,
    active: activeFilter?.col === col,
    hasFilter: col === 'period'
      ? !!(filters.periodStart || filters.periodEnd)
      : !!(filters[col]),
    onOpen: openFilter,
  });

  if (isLoading) {
    return <PageSpinner />;
  }

  if (isError) {
    return (
      <div className="tips-empty">
        <i className="fas fa-exclamation-circle" />
        <p>{t('admin.surveysError')}</p>
      </div>
    );
  }

  return (
    <>
      <div className="surveys-table">
        <div className="surveys-table-scroll">
          <table>
            <thead>
              <tr>
                {!hidden.has('name')      && <th><span className="col-flt-th">{t('admin.colName')}<FltBtn {...flt('name')} /></span></th>}
                {!hidden.has('questions') && <th>{t('admin.colQuestions')}</th>}
                {!hidden.has('status')    && <th><span className="col-flt-th">{t('admin.colStatus')}<FltBtn {...flt('status')} /></span></th>}
                {!hidden.has('audience')  && <th><span className="col-flt-th">{t('admin.colAudience')}<FltBtn {...flt('audience')} /></span></th>}
                {!hidden.has('frequency') && <th><span className="col-flt-th">{t('admin.colFrequency')}<FltBtn {...flt('frequency')} /></span></th>}
                {!hidden.has('createdBy') && <th>{t('admin.colCreatedBy')}</th>}
                {!hidden.has('feedback')  && <th>{t('admin.colFeedback')}</th>}
                {!hidden.has('period')    && <th><span className="col-flt-th">{t('admin.colPeriod')}<FltBtn {...flt('period')} /></span></th>}
                {!hidden.has('actions')   && <th>{t('admin.colActions')}</th>}
              </tr>
            </thead>
            <tbody>
              {!isLoading && sliced.length === 0 && (
                <tr>
                  <td colSpan={7} style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
                    <i className="fas fa-inbox" style={{ marginRight: 8 }} />
                    {t('admin.surveysNoResults')}
                  </td>
                </tr>
              )}
              {sliced.map(s => (
                <tr key={s.id}>
                  {!hidden.has('name') && (
                    <td>
                      <strong>{s.name}</strong>
                      {/* P4-6 — la descripción va bajo el nombre, como el prototipo. */}
                      {s.description && <span className="adm-survey-desc">{s.description}</span>}
                    </td>
                  )}
                  {!hidden.has('questions') && <td><span className="question-count">{s.questions}</span></td>}
                  {!hidden.has('status')    && <td><SurveyStatusBadge status={getStatus(s)} /></td>}
                  {!hidden.has('audience')  && <td>{formatOuAudience(s.ouIds, resolveOuName, t('admin.builderPublishGlobalLabel'))}</td>}
                  {!hidden.has('frequency') && <td>{frequencyLabel(t, s.activeDays)}</td>}
                  {!hidden.has('createdBy') && (
                    <td>
                      <CreatedByCell createdBy={s.createdBy} resolvePersonName={resolvePersonName} />
                    </td>
                  )}
                  {!hidden.has('feedback')  && (
                    <td>
                      <div className="mini-progress">
                        <span>{s.responses}</span>
                        <div className="mini-bar"><div style={{ width: `${Math.min(100, (s.responses / 500) * 100)}%` }} /></div>
                      </div>
                    </td>
                  )}
                  {!hidden.has('period') && (
                    <td>
                      {s.periodStart || s.periodEnd
                        ? <>
                            {s.periodStart && <>{formatDate(s.periodStart, i18n.language)} → </>}
                            {s.periodEnd && <strong>{formatDate(s.periodEnd, i18n.language)}</strong>}
                          </>
                        : s.period || '—'}
                    </td>
                  )}
                  {!hidden.has('actions') && (
                    <td>
                      <div className="adm-actions">
                        <button className="btn-icon" title={t('common.edit')} onClick={() => onOpenBuilder({ mode: 'edit', surveyId: s.id })}>
                          <i className="fas fa-pen" />
                        </button>
                        <button
                          className="btn-icon"
                          title={getStatus(s) === 'active' ? t('admin.pauseSurvey') : t('admin.activateSurvey')}
                          onClick={() => toggleStatus(s.id, getStatus(s))}
                        >
                          <i className={`fas fa-${getStatus(s) === 'active' ? 'pause' : 'play'}`} />
                        </button>
                        <button className="btn-icon" title={t('admin.duplicate')} disabled={mutationPending} onClick={() => onDuplicate?.(s.id)}>
                          {mutationPending ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-copy" />}
                        </button>
                        <button className="btn-icon success" title={t('admin.exportExcelTip')} onClick={() => onExportExcel?.(s.id)} disabled={!s.responses}><i className="fas fa-file-excel" /></button>
                        <button
                          className="btn-icon danger"
                          title={t('common.delete')}
                          disabled={mutationPending}
                          onClick={() => setConfirmOpts({
                            title: t('admin.confirmMoveToTrash'),
                            message: t('admin.confirmMoveToTrashMsg'),
                            confirmText: t('admin.confirmMoveBtn'),
                            onConfirm: () => onMoveToTrash(s.id, s.name, s.responses),
                          })}
                        >
                          {mutationPending ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-trash" />}
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pager
          total={sorted.length}
          page={page}
          pageSize={pageSize}
          onPage={setPage}
          onPageSize={s => { setPageSize(s); setPage(1); }}
        />
      </div>

      {activeFilter && (
        <ColFilterPopover
          col={activeFilter.col}
          top={activeFilter.top}
          left={activeFilter.left}
          filters={filters}
          onFilter={setFilter}
          onClose={() => setActiveFilter(null)}
        />
      )}

      {confirmOpts && (
        <ConfirmModal
          title={confirmOpts.title}
          message={confirmOpts.message}
          confirmText={confirmOpts.confirmText}
          onConfirm={confirmOpts.onConfirm}
          onClose={() => setConfirmOpts(null)}
        />
      )}

      {showTrash && (
        <TrashModal
          items={trash}
          onRestore={onRestoreFromTrash}
          onDeleteForever={onDeleteForever}
          onClose={onCloseTrash}
        />
      )}
    </>
  );
}
